import {
  createContext,
  useContext,
  useRef,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import { EventSourceClient } from '@/lib/eventsource';
import { Message } from '@/types';
interface MessageStreamContextType {
  sendMessage: (
    conversationId: string,
    content: string,
    sessionId: string,
    projectId: string,
    agentId?: string | null,
    options?: Record<string, any>,
    lastMessageId?: number
  ) => Promise<void>;
  stopRequest: (sessionId?: string) => string[];
  cleanupSessionMapping: (sessionId: string) => void;
  getConversationIdBySessionId: (sessionId: string) => string | undefined;
  updateSessionIdMapping: (sessionId: string, conversationId: string) => void;
  registerMessageHandler: (
    handlerId: string,
    handler: (message: Message) => void
  ) => void;
  unregisterMessageHandler: (handlerId: string) => void;
}
const MessageStreamContext = createContext<
  MessageStreamContextType | undefined
>(undefined);
// eslint-disable-next-line react-refresh/only-export-components
export function useMessageStreamContext() {
  const context = useContext(MessageStreamContext);
  if (!context) {
    throw new Error(
      'useMessageStreamContext must be used within MessageStreamProvider'
    );
  }
  return context;
}
interface MessageStreamProviderProps {
  children: ReactNode;
}
/**
 * MessageStreamProvider
 * 全局单例 EventSourceClient 管理器
 * 确保消息流不受组件挂载/卸载影响
 */
export function MessageStreamProvider({
  children,
}: MessageStreamProviderProps) {
  const esClient = useRef<EventSourceClient>();
  const sessionToConversationMapRef = useRef<Map<string, string>>(new Map());
  const messageHandlersRef = useRef<Map<string, (message: Message) => void>>(
    new Map()
  );
  const handleSessionUpdateRef =
    useRef<
      (
        frontendSessionId: string,
        backendSessionId: string,
        conversationId: string
      ) => void
    >();
  /**
   * 注册消息处理器
   */
  const registerMessageHandler = useCallback(
    (handlerId: string, handler: (message: Message) => void) => {
      messageHandlersRef.current.set(handlerId, handler);
    },
    []
  );
  /**
   * 注销消息处理器
   */
  const unregisterMessageHandler = useCallback((handlerId: string) => {
    messageHandlersRef.current.delete(handlerId);
  }, []);
  /**
   * 分发消息到所有注册的处理器
   */
  const dispatchMessage = useCallback((message: Message) => {
    messageHandlersRef.current.forEach((handler, handlerId) => {
      try {
        handler(message);
      } catch (error) {
        console.error(`Error in message handler ${handlerId}:`, error);
      }
    });
  }, []);
  /**
   * 当后端返回新的 sessionId 和 conversationId 时，更新映射
   */
  const handleSessionUpdate = useCallback(
    (
      frontendSessionId: string,
      backendSessionId: string,
      conversationId: string
    ) => {
      const tempConversationId =
        sessionToConversationMapRef.current.get(frontendSessionId);
      if (tempConversationId && tempConversationId.startsWith('temp-')) {
        sessionToConversationMapRef.current.set(
          backendSessionId,
          tempConversationId
        );
      } else {
        sessionToConversationMapRef.current.delete(frontendSessionId);
        sessionToConversationMapRef.current.set(
          backendSessionId,
          conversationId
        );
      }
    },
    []
  );
  // 保持 handleSessionUpdate 引用最新
  useEffect(() => {
    handleSessionUpdateRef.current = handleSessionUpdate;
  }, [handleSessionUpdate]);
  // Initialize EventSource client (only once, globally)
  useEffect(() => {
    esClient.current = new EventSourceClient('/api/stream', dispatchMessage);
    return () => {
      esClient.current?.stopRequest();
    };
  }, [dispatchMessage]);
  /**
   * 发送消息
   */
  const sendMessage = useCallback(
    async (
      content: string,
      conversationId: string,
      sessionId: string,
      projectId: string,
      agentId?: string | null,
      options?: Record<string, any>,
      lastMessageId?: number
    ) => {
      sessionToConversationMapRef.current.set(sessionId, conversationId);
      await esClient.current?.sendMessage(
        content,
        conversationId,
        sessionId,
        projectId,
        agentId,
        options,
        lastMessageId
      );
    },
    []
  );
  /**
   * 停止当前请求
   * @param sessionId 可选，指定要停止的 sessionId。如果不提供，则停止所有请求
   * @returns 所有被停止请求的 completionId 数组
   */
  const stopRequest = useCallback((sessionId?: string): string[] => {
    if (sessionId) {
      // 只取消指定的请求
      return esClient.current?.stopRequest(sessionId) || [];
    } else {
      // 取消所有请求（保留向后兼容）
      return esClient.current?.stopRequest() || [];
    }
  }, []);
  /**
   * 清理session映射
   */
  const cleanupSessionMapping = useCallback((sessionId: string) => {
    setTimeout(() => {
      sessionToConversationMapRef.current.delete(sessionId);
    }, 1000);
  }, []);
  /**
   * 根据sessionId获取conversationId
   */
  const getConversationIdBySessionId = useCallback((sessionId: string) => {
    return sessionToConversationMapRef.current.get(sessionId);
  }, []);
  /**
   * 更新sessionId映射的conversationId
   */
  const updateSessionIdMapping = useCallback(
    (sessionId: string, newConversationId: string) => {
      sessionToConversationMapRef.current.set(sessionId, newConversationId);
    },
    []
  );
  const value: MessageStreamContextType = {
    sendMessage,
    stopRequest,
    cleanupSessionMapping,
    getConversationIdBySessionId,
    updateSessionIdMapping,
    registerMessageHandler,
    unregisterMessageHandler,
  };
  return (
    <MessageStreamContext.Provider value={value}>
      {children}
    </MessageStreamContext.Provider>
  );
}
